import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { UserCircleIcon} from '@heroicons/react/24/solid';
import { useNavigate, useLocation } from "react-router-dom";

export default function SetPrint() {
    const API_URL = `${window.location.protocol}//${window.location.hostname}:5000`;

    const [students, setStudents] = useState([]);
    const [totalCount, setTotalCount] = useState(0);

    const [searchTerm, setSearchTerm] = useState('');
    const [resultsFound, setResultsFound] = useState(0);

    const [strand, setStrand] = useState('');
    const [section, setSection] = useState('');

    const [selectedIds, setSelectedIds] = useState([]);

    const navigate = useNavigate();
    const location = useLocation();

useEffect(() => {
  const params = new URLSearchParams(location.search);
  const search = params.get('search') || '';
  const strand = params.get('strand') || '';
  const section = params.get('section') || '';

  setSearchTerm(search);
  setStrand(strand);
  setSection(section);

  const loadStudents = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/students?search=${search}&strand=${strand}&section=${section}`);
      setStudents(res.data.students);

      if (search || strand || section) {
        setResultsFound(res.data.resultsFound ?? 0);
        setTotalCount(null);
      } else {
        setTotalCount(res.data.total ?? 0);
        setResultsFound(null);
      }
    } catch (err) {
      console.error('Error fetching students:', err);
    }
  };

  loadStudents();
}, [API_URL, location.search]);



    const fetchStudents = useCallback(async (search = '', strand = '', section = '') => {
      try {
        const res = await axios.get(`${API_URL}/api/students?search=${search}&strand=${strand}&section=${section}`);
        setStudents(res.data.students);

        if (search || strand || section) {
          setResultsFound(res.data.resultsFound ?? 0);
          setTotalCount(null); // hide totalCount when searching
        } else {
          setTotalCount(res.data.total ?? 0);
          setResultsFound(null); // hide resultsFound when not searching
        }
      } catch (err) {
        console.error('Error fetching students:', err);
      }
    }, [API_URL]);


    useEffect(() => {
      const delayDebounce = setTimeout(() => {
        fetchStudents(searchTerm, strand, section);
      }, 300); // delay for debounce typing

      return () => clearTimeout(delayDebounce);
    }, [fetchStudents, searchTerm, strand, section]); // re-trigger when search or strand changes


    const handleCheckboxChange = (id) => {
    
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const handleFetchSelected = () => {
    console.log("Selected IDs:", selectedIds);

        axios.post(`${API_URL}/api/students/bulk`, { ids: selectedIds })
        .then(res => {
        // ✅ Option A: pass the selected IDs & fetched data as route state
            navigate("/setprint/printid", {
                state: {
                ids: selectedIds,
                students: res.data   // optional: if you want to pass the fetched records
                }
            });
        })
        .catch(err => console.error(err));
    };

    return (
      <div className="px-4 mt-14">
        
          <div className="sticky top-12 z-10 w-full mx-auto bg-white flex flex-col lg:flex-row items-center gap-4 max-w-6xl pt-8 pb-4">
            <input className="w-full lg:flex-1 border border-gray-400 p-2 rounded" placeholder="Type name... (e.g. john)" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            <select
              value={strand}
              className="w-full flex-1 p-2 border border-gray-400 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 uppercase"
              onChange={(e) => setStrand(e.target.value)}
            >
              <option value="">-- Filter by Strand --</option>
              <option value="BAE">Business and Entrepreneurship (BAE)</option>
              <option value="ASSH">Arts, Social Sciences, and Humanities (ASSH)</option>
              <option value="ABM">Accountancy, Business, and Management (ABM)</option>
              <option value="HUMSS">Humanities and Social Sciences (HUMSS)</option>
              <option value="HE">Home Economics (HE)</option>
              <option value="ICT">Information and Communication Technology (ICT)</option>
            </select>
            <select
              value={section}
              className="w-full flex-1 p-2 border border-gray-400 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 uppercase"
              onChange={(e) => setSection(e.target.value)}
            >
              <option value="">-- Filter by Section --</option>
              <option value="jnc">JNC</option>
              <option value="dvt">DVT</option>
              <option value="etb">ETB</option>
              <option value="rlb">RLB</option>
              <option value="cpc">CPC</option>
              <option value="rbp">RBP</option>
              <option value="aag">AAG</option>
            </select>
            <button
              className="w-full lg:w-48 bg-gray-500 text-white p-3 rounded hover:bg-gray-400"
              onClick={() => {
                setSearchTerm('');
                setStrand('');
                setSection('');
                localStorage.removeItem('student-filters');
                navigate('/setprint', { replace: true });
                fetchStudents();
              }}
            >
              Clear Filter
            </button>
            <button
                className="w-full lg:w-48 bg-sky-900 text-white p-3 rounded hover:bg-sky-700"
                onClick={handleFetchSelected}
            >
                Print ID
            </button>
          </div>
      
        <div className="max-w-6xl mx-auto mb-12 rounded-lg border border-gray-500 p-2">
          <h2 className="text-2xl font-bold mt-4 text-center">Student List</h2>
          {resultsFound !== null ? (
            <p className="text-md font-bold mb-2">Results Found: {resultsFound}</p>
          ) : (
            <p className="text-md font-bold mb-2">Results Found: {totalCount}</p>
          )}

          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] table-auto border-collapse border border-gray-700 uppercase">
              <thead>
                <tr className="bg-gray-400 text-sky-900">
                  <th className="border border-sky-900 p-2">Profile</th>
                  <th className="border border-sky-900 p-2">Name</th>
                  <th className="border border-sky-900 p-2">Strand</th>
                  <th className="border border-sky-900 p-2">Section</th>
                  <th className="border border-sky-900 p-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(students) && students.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center p-4 text-gray-600">
                        ********** No records found **********.
                      </td>
                    </tr>
                ) : (
                  students?.map((s, index) => (
                    <tr key={index} className="hover:bg-gray-300 text-sm bg-gray-200">
                        <td className="border border-gray-400 p-2 text-center">
                            {s.profile ? (
                              <img
                                src={`${API_URL}/${s.profile}?t=${Date.now()}`}
                                alt="Profile"
                                className="w-16 h-16 object-cover rounded-full mx-auto shadow-md border border-gray-100"
                              />
                          ) : (
                            <div className="flex items-center justify-center">
                              <UserCircleIcon className="border border-gray-100 rounded-full h-16 w-16 text-gray-400"/>
                            </div>
                          )}
                        </td>
                        <td className="border border-gray-400 p-2">{s.lastname}, {s.firstname} {s.middlename}</td>
                        <td className="border border-gray-400 p-2 text-center">{s.strand}</td>
                        <td className="border border-gray-400 p-2 text-center">{s.section}</td>
                        <td className="border border-gray-400 p-2">
                            <div className="text-center">
                                <input className="w-6 h-6" type="checkbox" checked={selectedIds.includes(s.id)} onChange={() => handleCheckboxChange(s.id)}/>
                            </div>
                        </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
  );
};
